import { Box, Button, InAppSearchBox, SxProps } from "@outschool/backpack";
import { Search as SearchFilters } from "@outschool/business-rules";
import {
  AutoAppliedFilters,
  SearchFilters as SearchFiltersType
} from "@outschool/gql-backend-generated";
import { AutoAppliedFilterType } from "@outschool/gql-frontend-generated";
import { faSlidersH } from "@outschool/icons";
import {
  BASE_LOCALE,
  EAST_ASIA_LOCALES,
  useLocale,
  useTranslation
} from "@outschool/localization";
import {
  DAY_END_HOUR,
  DAY_START_HOUR,
  timeOfDayToStartEndTimes
} from "@outschool/time";
import {
  DisableAutoApplyFilters,
  Filter,
  FilterDropdown,
  FilterList,
  FundingProgramButton,
  SaveSearchButton,
  setDisabledAutoApplyFilter,
  useActivityString,
  useSearchFiltersString,
  useShowCredits
} from "@outschool/ui-components-website";
import {
  Checkbox,
  Container,
  OverlayBackdrop
} from "@outschool/ui-legacy-component-library";
import { Screen } from "@outschool/ui-utils";
import _ from "lodash";
import React, { useState } from "react";
import ReactDOM from "react-dom";
import { useDebouncedCallback } from "use-debounce/lib";

import * as Activity from "../../../../shared/Activity";
import {
  ExtendedSearchFiltersType,
  useSearchFilters
} from "../../../lib/useSearchFilters";
import AgeFilters from "../AgeFilters";
import CreditFilter from "../CreditFilter";
import DateFilters from "../DateFilters";
import EastAsiaSearchToggleButtons from "../EastAsiaSearchToggleButtons";
import EnglishProficiencyFilter from "../EnglishProficiencyFilter";
import ListingsSortBy from "../ListingsSortBy";
import PriceFilter from "../PriceFilter";
import SearchToggleButtons, {
  SearchToggleFiltersType
} from "../SearchToggleButtons";
import TimeFilters from "../TimeFilters";
import UncommonFilters from "../UncommonFilters";
import { emptyFilter } from "../useSearchWithSuggestions";
import DesktopSearchField from "./DesktopSearchField";
import { GradeLevelFilter } from "./LevelFilters";

type FilterChange = {
  filterCategory: string;
  partialFilters: object;
};

const filterDropdownSx: SxProps = {
  borderWidth: "2px"
};

type ListingFilterOpenDropdownOptions =
  | "age"
  | "gradeLevel"
  | "theme"
  | "schedule"
  | "date"
  | "englishProficiency"
  | "price"
  | "uncommon"
  | null;

export default function ListingFilters({
  searchPlaceholder,
  autoAppliedFilters,
  disabledAutoApplyFilters,
  setDisabledAutoApplyFilters,
  searchUid
}: {
  searchPlaceholder?: string;
  autoAppliedFilters?: AutoAppliedFilters;
  disabledAutoApplyFilters?: AutoAppliedFilterType[];
  setDisabledAutoApplyFilters?: DisableAutoApplyFilters;
  searchUid?: string;
} = {}) {
  const hasSubscription = useShowCredits();
  const { t } = useTranslation(
    "client\\components\\search\\desktop\\ListingFilters"
  );
  const locale = useLocale();
  const showEnglishProficiencyLevel = locale !== BASE_LOCALE;

  const {
    dateFiltersToString,
    getClassSizeFromCapacityFilter,
    englishProficiencyLevelToString
  } = useSearchFiltersString();
  const { translateThemeName } = useActivityString();
  const [openFilterDropdown, setOpenFilterDropdown] =
    React.useState<ListingFilterOpenDropdownOptions>(null);

  const isMobile = Screen.useIsMobile();

  const { filters: searchFilters, setFilters: setSearchFilters } =
    useSearchFilters();
  const [currentFilters, setCurrentFilters] = useState({
    ...searchFilters
  });

  const activeSearchToggle = searchFilters
    ? SearchFilters.activeSearchToggleFromFilters(searchFilters)
    : SearchFilters.SearchToggleType.ALL;

  if (searchFilters?.endBy && !searchFilters.startBefore) {
    setSearchFilters?.("Date", {
      endBy: undefined,
      startBefore: searchFilters?.endBy
    });
  }

  const updateSearchFilters = (
    filterCategory: $TSFixMe,
    partialFilters: $TSFixMe
  ) => {
    setFilterChanges({
      filterCategory: filterChanges?.filterCategory
        ? "MultiFilter"
        : filterCategory,
      partialFilters: !filterChanges
        ? partialFilters
        : { ...filterChanges.partialFilters, ...partialFilters }
    });
  };

  const [filterChanges, setFilterChanges] = useState<FilterChange | null>(null);

  const closeFilterDropdown = () => {
    if (filterChanges) {
      setSearchFilters?.(
        filterChanges.filterCategory,
        filterChanges.partialFilters
      );
      setFilterChanges(null);
    }

    setOpenFilterDropdown(null);
  };

  const handleFilterChangeAndCloseFilterDropdown = (
    category: string,
    value: object
  ) => {
    setCurrentFilters({ ...currentFilters, ...value });
    setSearchFilters?.(category, value);
    setOpenFilterDropdown(null);
  };

  React.useEffect(() => {
    const escapeFilter = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        if (filterChanges) {
          setFilterChanges(null);
        }
        if (openFilterDropdown) {
          setOpenFilterDropdown(null);
        }
      }
    };

    window.addEventListener("keydown", escapeFilter);

    return () => {
      window.removeEventListener("keydown", escapeFilter);
    };
  }, [filterChanges, openFilterDropdown]);

  const handleImplicitFilterChange = (category: string, value: object) => {
    updateSearchFilters(category, value);
    setCurrentFilters({ ...currentFilters, ...value });
  };

  const [isScrollBeingListened, setIsScrollBeingListened] = useState(false);
  const [debouncedCloseFilterDropdown] = useDebouncedCallback(() => {
    closeFilterDropdown();
  }, 100);
  if (openFilterDropdown && !isScrollBeingListened) {
    window.addEventListener("scroll", debouncedCloseFilterDropdown);
    setIsScrollBeingListened(true);
  }
  if (!openFilterDropdown && isScrollBeingListened) {
    window.removeEventListener("scroll", debouncedCloseFilterDropdown);
    setIsScrollBeingListened(false);
  }

  if (!filterChanges && !_.isEqual(currentFilters, searchFilters)) {
    // @ts-ignore TS(2345): Argument of type 'ExtendedSearchFiltersType | unde... Remove this comment to see the full error message
    setCurrentFilters(searchFilters);
  }

  const {
    showChatGroupInsideClassType,
    showClassSizeFilter,
    showClassTypeFilterInUncommonFilters,
    showCurriculums,
    showEvaluation,
    showGradeFilterOnMainBar,
    showStandards
  } = SearchFilters.useContextualFilters({
    activeSearchToggle,
    isMobile: false
  });

  const containerRef = React.useRef();
  const themes = currentFilters.theme ? currentFilters.theme.split(",") : [];
  const themesSummary = themes.map(translateThemeName).join(", ");
  const formats = currentFilters.format ? currentFilters.format.split(",") : [];
  const deliveries = currentFilters.delivery
    ? currentFilters.delivery.split(",")
    : [];

  const ages = currentFilters.age
    ? _.uniq(
        currentFilters.age
          .split(",")
          .map(age => +age)
          .filter(age => age <= Activity.ageMax)
      )
    : [];

  const evaluationFilters = [
    currentFilters.hasHomework && "hasHomework",
    currentFilters.hasAssessment && "hasAssessment",
    currentFilters.hasGrades && "hasGrades"
  ].filter(Boolean) as string[];

  const enabledLanguageFilters = currentFilters.languageOfInstruction
    ? currentFilters.languageOfInstruction.split(",")
    : [];

  const enabledCurriculumFilters = currentFilters.curriculums
    ? currentFilters.curriculums
    : [];

  const enabledStandardFilters = currentFilters.standards
    ? currentFilters.standards
    : [];

  const gradeLevels = React.useMemo(() => {
    const defaultGradeLevels = currentFilters.gradeLevel
      ? currentFilters.gradeLevel.split(",")
      : [];

    if (disabledAutoApplyFilters?.includes(AutoAppliedFilterType.GradeLevels)) {
      return defaultGradeLevels;
    }

    return _.uniq(
      _.merge(defaultGradeLevels, autoAppliedFilters?.gradeLevels ?? [])
    ).sort();
  }, [
    autoAppliedFilters?.gradeLevels,
    currentFilters.gradeLevel,
    disabledAutoApplyFilters
  ]);

  // NOTE: Class type filters are a combination of format + possibly a subset of delivery filters
  const classTypes = [
    ...formats,
    ...deliveries.filter(str => str === Activity.ACTIVITY_DELIVERY.CLUB)
  ];

  const capacityFilters: SearchFilters.CapacityFilterType = _.omitBy(
    _.pick(currentFilters, SearchFilters.CAPACITY_FILTERS),
    _.isNull
  );

  const classSizeValue = getClassSizeFromCapacityFilter(capacityFilters);
  const classSizeValueWithoutAny =
    classSizeValue === "any" ? null : classSizeValue;

  const uncommonFilters = [
    ...(showClassTypeFilterInUncommonFilters ? classTypes : []),
    ...(!showGradeFilterOnMainBar ? gradeLevels : []),
    showClassSizeFilter ? classSizeValueWithoutAny : null,
    ...evaluationFilters,
    ...enabledLanguageFilters,
    ...(showCurriculums ? enabledCurriculumFilters : []),
    ...(showStandards ? enabledStandardFilters : [])
  ].filter(filter => !_.isEmpty(filter));

  if (searchFilters?.timeOfDay) {
    const { startAfterTime, endByTime } = timeOfDayToStartEndTimes(
      searchFilters.timeOfDay
    );
    searchFilters.startAfterTime = startAfterTime;
    searchFilters.endByTime = endByTime;
    delete searchFilters.timeOfDay;
  }

  const dateFilters = _.pick(currentFilters, [...SearchFilters.DATE_FILTERS]);
  const scheduleFilters = _.pick(currentFilters, SearchFilters.TIME_FILTERS);
  const numScheduleFiltersSelected = Object.keys(scheduleFilters).filter(
    key => {
      if (key === "endByTime" && scheduleFilters[key] === DAY_END_HOUR) {
        return false;
      }
      if (key === "startAfterTime" && scheduleFilters[key] === DAY_START_HOUR) {
        return false;
      }
      // @ts-ignore
      return !!scheduleFilters[key];
    }
  ).length;

  const priceFilters = _.omitBy(
    _.pick(
      currentFilters,
      SearchFilters.PRICE_PER_COURSE_FILTERS.concat(
        SearchFilters.PRICE_PER_MEETING_FILTERS
      )
    ),
    _.isNull
  );

  const creditFilters = _.omitBy(
    _.pick(
      currentFilters,
      SearchFilters.PRICE_CREDITS_PER_MEETING_FILTERS.concat(
        SearchFilters.PRICE_CREDITS_PER_COURSE_FILTERS
      )
    ),
    _.isNull
  );

  const tabFilters = SearchFilters.getToggleButtonFilters(currentFilters);
  return (
    <>
      {openFilterDropdown &&
        ReactDOM.createPortal(
          <OverlayBackdrop onClick={closeFilterDropdown} />,
          document.body
        )}
      <Box
        onClick={(e: $TSFixMe) => {
          if (e.target === e.currentTarget) {
            closeFilterDropdown();
          }
        }}
        sx={{
          display: "flex",
          flexDirection: "column",
          gap: "1em",
          paddingY: "1em"
        }}
        ref={containerRef}
      >
        <Container fullBleed>
          {setSearchFilters && EAST_ASIA_LOCALES.includes(locale) ? (
            <EastAsiaSearchToggleButtons
              setSearchFilters={setSearchFilters}
              clearDropdowns={() => {
                if (openFilterDropdown) {
                  closeFilterDropdown();
                }
              }}
            />
          ) : (
            <SearchToggleButtons
              searchFilters={tabFilters}
              onChange={(searchToggleFilters: SearchToggleFiltersType) => {
                const newFilterValues =
                  SearchFilters.newFiltersFromSearchToggleChange(
                    activeSearchToggle,
                    searchToggleFilters
                  );
                setSearchFilters?.("Capacity", newFilterValues, {});
                setSearchFilters?.("Uncommon", newFilterValues, {});
              }}
              clearDropdowns={() => {
                if (openFilterDropdown) {
                  closeFilterDropdown();
                }
              }}
            />
          )}
        </Container>
        <Container>
          <DesktopSearchField
            searchPlaceholder={searchPlaceholder}
            searchFilters={currentFilters}
            setImplicitSearchFilters={filters =>
              handleImplicitFilterChange("Query", filters)
            }
            setSearchFilters={(filters: ExtendedSearchFiltersType) => {
              handleImplicitFilterChange("Query", filters);

              if (!openFilterDropdown) {
                setSearchFilters?.("Query", filters);
              }
            }}
            clearDropdowns={() => {
              if (openFilterDropdown) {
                closeFilterDropdown();
              }
            }}
            SearchBoxComponent={InAppSearchBox}
            showSearchSuggestions={false}
          />
        </Container>
        <Container
          fullBleed={isMobile}
          sx={{ display: "flex", justifyContent: "space-between" }}
        >
          <Box
            sx={{
              width: "100%",
              display: "flex",
              justifyContent: "space-between"
            }}
          >
            <Box
              flex
              onClick={(e: $TSFixMe) => {
                if (e.target === e.currentTarget) {
                  closeFilterDropdown();
                }
              }}
              sx={{
                gap: "0.5em",
                flexWrap: "wrap"
              }}
            >
              <FundingProgramButton
                searchUid={searchUid}
                searchFilters={searchFilters}
                updateSearchFilters={(payload: SearchFiltersType) =>
                  setSearchFilters?.("FundingPrograms", payload)
                }
              />
              <FilterDropdown
                filterName={t("Age")}
                filterValue={
                  ages.length
                    ? t("Age {{ages}}", {
                        count: ages.length,
                        ages: SearchFilters.agesArrayToString(
                          ages.map(a => a.toString())
                        ),
                        defaultValue_plural: "Ages {{ages}}"
                      })
                    : undefined
                }
                open={openFilterDropdown === "age"}
                onClose={closeFilterDropdown}
                onOpen={() => setOpenFilterDropdown("age")}
                referenceElement={isMobile ? containerRef.current : undefined}
                buttonSx={filterDropdownSx}
              >
                <AgeFilters
                  value={ages}
                  handleChangesExternally
                  onChange={value => {
                    const newFilterValues = {
                      age: value.length ? value.join(",") : null
                    };
                    handleImplicitFilterChange("Age", newFilterValues);
                  }}
                  onCancel={() => {}}
                />
              </FilterDropdown>
              {showGradeFilterOnMainBar && (
                <GradeLevelFilter
                  value={gradeLevels}
                  onChange={value => {
                    if (
                      autoAppliedFilters &&
                      disabledAutoApplyFilters &&
                      setDisabledAutoApplyFilters
                    ) {
                      setDisabledAutoApplyFilter({
                        disableFilterType: AutoAppliedFilterType.GradeLevels,
                        autoAppliedFilters,
                        disabledAutoApplyFilters,
                        setDisabledAutoApplyFilters
                      });
                    }
                    const newFilterValues = {
                      gradeLevel: value.length ? value.join(",") : null
                    };
                    handleImplicitFilterChange("GradeLevel", newFilterValues);
                  }}
                  openFilterDropdown={openFilterDropdown}
                  onClose={closeFilterDropdown}
                  onOpen={() => setOpenFilterDropdown("gradeLevel")}
                  referenceElement={isMobile ? containerRef.current : undefined}
                  filterDropdownSx={filterDropdownSx}
                />
              )}
              <FilterDropdown
                filterName={t("Subject")}
                filterValue={
                  themesSummary.length < 20
                    ? themesSummary
                    : t("{{numSubjects}} subject", {
                        count: themes.length,
                        numSubjects: themes.length,
                        defaultValue_plural: "{{numSubjects}} subjects"
                      })
                }
                open={openFilterDropdown === "theme"}
                onClose={closeFilterDropdown}
                onOpen={() => setOpenFilterDropdown("theme")}
                referenceElement={isMobile ? containerRef.current : undefined}
                buttonSx={filterDropdownSx}
              >
                <ThemeFilter
                  value={themes}
                  onChange={value => {
                    const newFilterValues = {
                      theme: value.length ? value.join(",") : null
                    };

                    handleImplicitFilterChange("Theme", newFilterValues);
                  }}
                  selectedSearchToggle={activeSearchToggle}
                />
              </FilterDropdown>
              <FilterDropdown
                filterName={t("Schedule")}
                filterValue={
                  numScheduleFiltersSelected > 0 ? t("Schedule") : undefined
                }
                open={openFilterDropdown === "schedule"}
                onClose={closeFilterDropdown}
                onOpen={() => setOpenFilterDropdown("schedule")}
                referenceElement={isMobile ? containerRef.current : undefined}
                buttonSx={filterDropdownSx}
              >
                <TimeFilters
                  value={scheduleFilters}
                  onChange={result => {
                    handleImplicitFilterChange("Schedule", result);
                  }}
                  handleChangesExternally
                  onCancel={() => {}}
                />
              </FilterDropdown>
              <FilterDropdown
                filterName={t("Date")}
                filterValue={dateFiltersToString(dateFilters)}
                open={openFilterDropdown === "date"}
                onClose={closeFilterDropdown}
                onOpen={() => setOpenFilterDropdown("date")}
                referenceElement={isMobile ? containerRef.current : undefined}
                buttonSx={filterDropdownSx}
              >
                <DateFilters
                  value={dateFilters}
                  handleChangesExternally
                  onShortcutSelected={result => {
                    // eslint-disable-next-line i18next/no-literal-string
                    handleFilterChangeAndCloseFilterDropdown("Date", result);
                  }}
                  onChange={result => {
                    handleImplicitFilterChange("Date", result);
                  }}
                  onCancel={closeFilterDropdown}
                />
              </FilterDropdown>
              {showEnglishProficiencyLevel && (
                <FilterDropdown
                  filterName={t("English Level")}
                  filterValue={englishProficiencyLevelToString(
                    currentFilters.englishProficiencyLevel
                  )}
                  buttonSx={filterDropdownSx}
                  open={openFilterDropdown === "englishProficiency"}
                  onClose={closeFilterDropdown}
                  onOpen={() => setOpenFilterDropdown("englishProficiency")}
                >
                  <Filter title={t("English Level")}>
                    <EnglishProficiencyFilter
                      onChange={level =>
                        handleImplicitFilterChange("EnglishProficiency", {
                          englishProficiencyLevel: level
                        })
                      }
                      currentFilterProficiencyLevel={
                        currentFilters.englishProficiencyLevel
                      }
                    />
                  </Filter>
                </FilterDropdown>
              )}
              {hasSubscription ? (
                <CreditFilter
                  creditFilterValue={creditFilters}
                  handleChangesExternally
                  open={openFilterDropdown === "price"}
                  onOpen={() => setOpenFilterDropdown("price")}
                  onClose={closeFilterDropdown}
                  buttonSx={filterDropdownSx}
                  handleImplicitFilterChange={handleImplicitFilterChange}
                  isCourse={
                    activeSearchToggle ===
                    SearchFilters.SearchToggleType.COURSES
                  }
                />
              ) : (
                <PriceFilter
                  priceFilterValue={priceFilters}
                  handleChangesExternally
                  selectedSearchToggle={activeSearchToggle}
                  open={openFilterDropdown === "price"}
                  onOpen={() => setOpenFilterDropdown("price")}
                  onClose={closeFilterDropdown}
                  buttonSx={filterDropdownSx}
                  handleImplicitFilterChange={handleImplicitFilterChange}
                />
              )}
              <FilterDropdown
                showChevron={false}
                filterIcon={faSlidersH}
                filterValue={
                  uncommonFilters.length > 0
                    ? t("+{{uncommonFilterCount}} filter", {
                        uncommonFilterCount: uncommonFilters.length,
                        count: uncommonFilters.length,
                        defaultValue_plural: "+{{uncommonFilterCount}} filters"
                      })
                    : undefined
                }
                open={openFilterDropdown === "uncommon"}
                onClose={closeFilterDropdown}
                onOpen={() => setOpenFilterDropdown("uncommon")}
                referenceElement={isMobile ? containerRef.current : undefined}
                buttonSx={{
                  ...filterDropdownSx
                }}
              >
                <UncommonFilters
                  hideFormat
                  hideClassType={!showClassTypeFilterInUncommonFilters}
                  hideUSGrade={showGradeFilterOnMainBar}
                  hideChatGroupInsideClassType={!showChatGroupInsideClassType}
                  hideEvaluation={!showEvaluation}
                  hideClassSize={!showClassSizeFilter}
                  hideCurriculums={!showCurriculums}
                  hideStandards={!showStandards}
                  handleChangesExternally
                  value={{
                    enabledFormatFilters: formats,
                    enabledDeliveryFilters: deliveries,
                    enabledLanguageFilters,
                    enabledUSGradeLevelFilters: gradeLevels,
                    enabledEvaluationFilters: evaluationFilters,
                    enabledClassSizeFilters: capacityFilters,
                    enabledCurriculumFilters,
                    enabledStandardFilters,
                    ...(typeof currentFilters.includeInProgressFixedLengthLiveFormat ===
                      "boolean" && {
                      includeInProgressFixedLengthLiveFormat:
                        currentFilters.includeInProgressFixedLengthLiveFormat
                    })
                  }}
                  onChange={({
                    enabledFormatFilters,
                    enabledDeliveryFilters,
                    enabledLanguageFilters,
                    enabledUSGradeLevelFilters,
                    enabledEvaluationFilters,
                    enabledClassSizeFilters,
                    enabledCurriculumFilters,
                    enabledStandardFilters,
                    includeInProgressFixedLengthLiveFormat
                  }) => {
                    const newFilterValues = {
                      format: enabledFormatFilters?.length
                        ? enabledFormatFilters.join(",")
                        : null,
                      delivery: enabledDeliveryFilters?.length
                        ? enabledDeliveryFilters.join(",")
                        : null,
                      languageOfInstruction: enabledLanguageFilters?.length
                        ? enabledLanguageFilters.join(",")
                        : null,
                      curriculums: enabledCurriculumFilters?.length
                        ? enabledCurriculumFilters
                        : null,
                      standards: enabledStandardFilters?.length
                        ? enabledStandardFilters
                        : null,
                      gradeLevel: enabledUSGradeLevelFilters?.length
                        ? enabledUSGradeLevelFilters.join(",")
                        : null,
                      hasHomework: enabledEvaluationFilters?.includes(
                        "hasHomework"
                      )
                        ? true
                        : null,
                      hasAssessment: enabledEvaluationFilters?.includes(
                        "hasAssessment"
                      )
                        ? true
                        : null,
                      hasGrades: enabledEvaluationFilters?.includes("hasGrades")
                        ? true
                        : null,
                      capacityMin: enabledClassSizeFilters?.capacityMin,
                      capacityMax: enabledClassSizeFilters?.capacityMax,
                      includeInProgressFixedLengthLiveFormat
                    };

                    // If search toggle needs to change, reset some filters.
                    const newFiltersValuesIfToggleChange =
                      SearchFilters.newFiltersFromSearchToggleChange(
                        activeSearchToggle,
                        newFilterValues
                      );
                    handleImplicitFilterChange(
                      "Uncommon",
                      newFiltersValuesIfToggleChange
                    );
                  }}
                  onCancel={() => {}}
                />
              </FilterDropdown>
              <Button
                variant="link"
                sx={{ color: "#262626" }}
                onClick={(e: $TSFixMe) => {
                  const activeToggleFilters =
                    SearchFilters.SEARCH_TOGGLE_TO_FILTERS[activeSearchToggle];
                  const filtersToRetain = [
                    ...new Set([
                      ...SearchFilters.QUERY_FILTERS,
                      ...Object.keys(activeToggleFilters)
                    ])
                  ];

                  setSearchFilters?.("Reset", {
                    ..._.omit(emptyFilter, filtersToRetain)
                  });
                  setCurrentFilters(cf => {
                    return _.pick(cf, filtersToRetain);
                  });
                  setOpenFilterDropdown(null);
                  (e.currentTarget as HTMLElement).blur();
                }}
              >
                {t("Reset")}
              </Button>
            </Box>
            <Box flex sx={{ marginLeft: "1em", gap: "0.5em" }}>
              <ListingsSortBy
                // @ts-ignore TS(2322): Type 'ExtendedSearchFiltersType | undefined' is no... Remove this comment to see the full error message
                filters={searchFilters}
                setFilters={setSearchFilters}
                useFilterDropdownStyling={true}
              />
              <SaveSearchButton filters={searchFilters} variant="large" />
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
}

export function ThemeFilter({
  value,
  onChange,
  selectedSearchToggle
}: {
  value: string[];
  onChange: (value: string[]) => void;
  selectedSearchToggle: SearchFilters.SearchToggleType;
}) {
  const { t } = useTranslation(
    "client\\components\\search\\desktop\\ListingFilters"
  );
  const { translateThemeName } = useActivityString();
  const [themes, setThemes] = React.useState(value || []);
  const handleFilterChange = (value: string[]) => {
    setThemes(value);
    onChange(value);
  };

  const subjects =
    SearchFilters.getSubjectForSearchToggle(selectedSearchToggle);

  return (
    <FilterList>
      <Filter title={t("Subject")}>
        {subjects.map(theme => (
          <Checkbox
            key={theme}
            id={theme}
            label={translateThemeName(theme)}
            checked={!!themes.find(t => t === theme)}
            onChange={e => {
              const checked = e.target.checked;
              handleFilterChange(
                checked ? [...themes, theme] : _.without(themes, theme)
              );
            }}
            sx={{ marginBottom: "small" }}
          />
        ))}
      </Filter>
    </FilterList>
  );
}
